/**
 * Opens a new window with a print-ready 4x6" shipping-label view.
 * The label is auto-sized (@page 4in 6in) so it prints on standard thermal
 * shipping-label printers (Rollo, DYMO, Zebra) without user configuration.
 */
function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

export function openPrintLabel(order) {
  const addr = order.shipping_address || {};
  const name = order.name || addr.full_name || "";
  const phone = order.phone || addr.phone || "";
  const cityLine = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ");

  const items = Array.isArray(order.line_items) && order.line_items.length
    ? order.line_items.map((li) => `${esc(li.name)} &times; ${esc(li.quantity)}`).join("<br />")
    : (order.items || []).map(esc).join("<br />");

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Shipping label — ${esc(order.session_id || "")}</title>
<style>
  @page { size: 4in 6in; margin: 0.15in; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
    color: #0F0F10;
    padding: 0.15in;
    width: 3.7in;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .brand {
    display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 2px solid #0F0F10; padding-bottom: 6px; margin-bottom: 10px;
  }
  .brand-name { font-weight: 800; font-size: 15pt; letter-spacing: -0.02em; }
  .brand-tag  { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.14em; }
  .from       { font-size: 8pt; line-height: 1.35; color: #444; margin-bottom: 12px; }
  .label      { font-size: 7pt; text-transform: uppercase; letter-spacing: 0.14em; color: #555; margin-bottom: 4px; }
  .to-name    { font-weight: 700; font-size: 15pt; line-height: 1.2; }
  .to-addr    { font-size: 12pt; line-height: 1.35; margin-top: 4px; }
  .to-phone   { font-size: 10pt; margin-top: 6px; color: #444; }
  .divider    { border: 0; border-top: 1px dashed #999; margin: 14px 0; }
  .meta-grid  { display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; font-size: 8pt; }
  .meta-grid dt { color: #666; text-transform: uppercase; letter-spacing: 0.08em; font-size: 7pt; }
  .meta-grid dd { margin: 0; font-family: "Courier New", monospace; word-break: break-all; }
  .items-title { margin-top: 12px; }
  .items       { font-size: 9pt; line-height: 1.55; }
  .footer-note { position: fixed; bottom: 0.18in; left: 0.15in; right: 0.15in;
                 font-size: 7pt; color: #777; border-top: 1px solid #ccc; padding-top: 4px; }
  @media screen {
    body { max-width: 4in; margin: 20px auto; box-shadow: 0 4px 24px rgba(0,0,0,0.15); background: #fff; }
    .toolbar { position: fixed; top: 12px; right: 12px; z-index: 10; display: flex; gap: 8px; }
    .toolbar button { background: #4ADE80; color: #0F0F10; border: 0; padding: 8px 14px;
                      font: 600 12px sans-serif; letter-spacing: 0.12em; text-transform: uppercase; cursor: pointer; }
    .toolbar button.ghost { background: transparent; border: 1px solid #ccc; color: #333; }
  }
  @media print { .toolbar { display: none !important; } }
</style>
</head>
<body>
  <div class="toolbar">
    <button class="ghost" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print</button>
  </div>

  <div class="brand">
    <div class="brand-name">EndaG1</div>
    <div class="brand-tag">Ship Label</div>
  </div>

  <div class="from">
    <strong>FROM:</strong> EndaG1 · orders@endag1.com
  </div>

  <div class="label">Ship to</div>
  <div class="to-name">${esc(name)}</div>
  <div class="to-addr">
    ${addr.street ? esc(addr.street) + "<br />" : ""}
    ${addr.apartment ? esc(addr.apartment) + "<br />" : ""}
    ${cityLine ? esc(cityLine) + "<br />" : ""}
    ${addr.country ? esc(addr.country) : ""}
  </div>
  ${phone ? `<div class="to-phone">${esc(phone)}</div>` : ""}

  <hr class="divider" />

  <dl class="meta-grid">
    <dt>Order</dt>       <dd>${esc((order.session_id || "").slice(0, 20))}${(order.session_id || "").length > 20 ? "…" : ""}</dd>
    <dt>Date</dt>        <dd>${esc((order.created_at || "").slice(0, 10))}</dd>
    <dt>Method</dt>      <dd>${esc(order.method || "card")}</dd>
    <dt>Amount</dt>      <dd>${Number(order.amount || 0).toFixed(2)} ${esc((order.currency || "usd").toUpperCase())}</dd>
    ${order.tracking_number ? `<dt>Tracking</dt><dd>${esc(order.tracking_number)}</dd>` : ""}
  </dl>

  <div class="label items-title">Contents</div>
  <div class="items">${items}</div>

  <div class="footer-note">Research Use Only · Not for human or veterinary use.</div>

  <script>
    // Auto-invoke print after render so common workflows are one-click.
    window.addEventListener("load", () => setTimeout(() => window.print(), 250));
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=520,height=760");
  if (!w) {
    // Pop-up blocker fallback: open as blob
    const blob = new Blob([html], { type: "text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}


/**
 * Print every unshipped order in a single window — one label per page.
 * Perfect for the morning fulfillment session: click once, feed labels
 * to the printer, and every order comes out sequentially.
 */
export function openPrintAllLabels(orders) {
  if (!orders || orders.length === 0) return;
  const pages = orders.map((o, idx) => {
    const addr = o.shipping_address || {};
    const name = o.name || addr.full_name || "";
    const phone = o.phone || addr.phone || "";
    const cityLine = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ");
    const items = Array.isArray(o.line_items) && o.line_items.length
      ? o.line_items.map((li) => `${esc(li.name)} &times; ${esc(li.quantity)}`).join("<br />")
      : (o.items || []).map(esc).join("<br />");
    return `
      <div class="page">
        <div class="brand"><div class="brand-name">EndaG1</div><div class="brand-tag">Ship Label · ${idx + 1}/${orders.length}</div></div>
        <div class="from"><strong>FROM:</strong> EndaG1 · orders@endag1.com</div>
        <div class="label">Ship to</div>
        <div class="to-name">${esc(name)}</div>
        <div class="to-addr">
          ${addr.street ? esc(addr.street) + "<br />" : ""}
          ${addr.apartment ? esc(addr.apartment) + "<br />" : ""}
          ${cityLine ? esc(cityLine) + "<br />" : ""}
          ${addr.country ? esc(addr.country) : ""}
        </div>
        ${phone ? `<div class="to-phone">${esc(phone)}</div>` : ""}
        <hr class="divider" />
        <dl class="meta-grid">
          <dt>Order</dt><dd>${esc((o.session_id || "").slice(0, 20))}${(o.session_id || "").length > 20 ? "…" : ""}</dd>
          <dt>Date</dt><dd>${esc((o.created_at || "").slice(0, 10))}</dd>
          <dt>Amount</dt><dd>${Number(o.amount || 0).toFixed(2)} ${esc((o.currency || "usd").toUpperCase())}</dd>
          ${o.tracking_number ? `<dt>Tracking</dt><dd>${esc(o.tracking_number)}</dd>` : ""}
        </dl>
        <div class="label items-title">Contents</div>
        <div class="items">${items}</div>
      </div>`;
  }).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Batch labels · ${orders.length}</title>
  <style>
    @page { size: 4in 6in; margin: 0.15in; }
    html, body { margin: 0; padding: 0; }
    body { font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #0F0F10; }
    .page { padding: 0.15in; width: 3.7in; page-break-after: always; box-sizing: border-box; }
    .page:last-child { page-break-after: auto; }
    .brand { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 2px solid #0F0F10; padding-bottom: 6px; margin-bottom: 10px; }
    .brand-name { font-weight: 800; font-size: 15pt; }
    .brand-tag { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.14em; }
    .from { font-size: 8pt; line-height: 1.35; color: #444; margin-bottom: 12px; }
    .label { font-size: 7pt; text-transform: uppercase; letter-spacing: 0.14em; color: #555; margin-bottom: 4px; }
    .to-name { font-weight: 700; font-size: 15pt; line-height: 1.2; }
    .to-addr { font-size: 12pt; line-height: 1.35; margin-top: 4px; }
    .to-phone { font-size: 10pt; margin-top: 6px; color: #444; }
    .divider { border: 0; border-top: 1px dashed #999; margin: 14px 0; }
    .meta-grid { display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; font-size: 8pt; }
    .meta-grid dt { color: #666; text-transform: uppercase; letter-spacing: 0.08em; font-size: 7pt; }
    .meta-grid dd { margin: 0; font-family: "Courier New", monospace; word-break: break-all; }
    .items-title { margin-top: 12px; }
    .items { font-size: 9pt; line-height: 1.55; }
    @media screen {
      body { background: #eee; padding: 20px 0; }
      .page { max-width: 4in; margin: 12px auto; background: #fff; box-shadow: 0 4px 24px rgba(0,0,0,0.15); }
      .toolbar { position: fixed; top: 12px; right: 12px; z-index: 10; display: flex; gap: 8px; }
      .toolbar button { background: #4ADE80; color: #0F0F10; border: 0; padding: 8px 14px; font: 600 12px sans-serif; letter-spacing: 0.12em; text-transform: uppercase; cursor: pointer; }
      .toolbar button.ghost { background: transparent; border: 1px solid #ccc; color: #333; }
    }
    @media print { .toolbar { display: none !important; } }
  </style></head><body>
    <div class="toolbar">
      <button class="ghost" onclick="window.close()">Close</button>
      <button onclick="window.print()">Print ${orders.length} labels</button>
    </div>
    ${pages}
    <script>window.addEventListener("load",()=>setTimeout(()=>window.print(),300));</script>
  </body></html>`;

  const w = window.open("", "_blank", "width=560,height=800");
  if (!w) {
    const blob = new Blob([html], { type: "text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
